import { EntityLinks, GoalProgressCount } from '@griply/types';
import { getGoalAncestors } from '@griply/utils';
import { IconFormNames } from 'shared/assets/icons';
import { Paths } from 'shared/routes/paths';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { getGoalDetailUrl } from 'shared/utils/get-goal-detail-url';

export type GoalBreadcrumbItem = {
  id: ID;
  name: string;
  path: string;
  percentage: number;
  hasProgress: boolean;
  iconName?: IconFormNames | null;
  isGoal: boolean;
  isCurrent: boolean;
};

// Ordered root → current: [lifeArea?, ...goalAncestors, current]. The consumer
// keeps the last ancestor + current visible and collapses the rest behind "…".
export const getGoalBreadcrumbTrail = (
  goal: Goal,
  goals: Goal[],
  lifeAreas: LifeArea[],
  goalProgressCounts?: Map<ID, GoalProgressCount>,
  links?: EntityLinks,
): GoalBreadcrumbItem[] => {
  const ancestors = getGoalAncestors(goal, goals, { links });

  const toGoalItem = (item: Goal, isCurrent: boolean): GoalBreadcrumbItem => {
    const { start, current, end } = getGoalCompletion(item, {
      goals,
      goalProgressCounts,
      links,
    });

    return {
      id: item.id,
      name: item.name,
      path: getGoalDetailUrl(item),
      percentage: getCompletionPercentage({ start, current, end }),
      hasProgress: end !== Infinity && !item.completedAt,
      iconName: item.iconName,
      isGoal: true,
      isCurrent,
    };
  };

  const items: GoalBreadcrumbItem[] = [
    ...ancestors.map((ancestor) => toGoalItem(ancestor, false)),
    toGoalItem(goal, true),
  ];

  const rootGoal = ancestors[0] ?? goal;
  const lifeArea = lifeAreas.find(({ id }) => id === rootGoal.lifeAreaId);

  if (lifeArea) {
    items.unshift({
      id: lifeArea.id,
      name: lifeArea.name,
      path: Paths.LifeAreaDetail.replace(':id', lifeArea.id),
      percentage: 0,
      hasProgress: false,
      iconName: lifeArea.iconName,
      isGoal: false,
      isCurrent: false,
    });
  }

  return items;
};
